<script setup>
/**
 * UploadField — file picker that uploads on selection and emits the hosted URL via v-model.
 */
import { ref, computed } from "vue";
import { useUpload } from "@/composables/useUpload";

const props = defineProps({
  modelValue: { type: String,   default: "" },
  uploader:   { type: Function, required: true },
  label:      { type: String,   default: "File" },
  kind:       { type: String,   default: "file" }, // image | video | file
  accept:     { type: String,   default: "" },
  hint:       { type: String,   default: "" },
});
const emit = defineEmits(["update:modelValue", "select"]);

const inputEl = ref(null);
const dragOver = ref(false);
const fileName = ref("");

const { uploading, progress, error, upload } = useUpload(props.uploader, { kind: props.kind });

const defaultAccept = computed(() =>
  props.accept || (props.kind === "image" ? "image/*" : props.kind === "video" ? "video/*" : "")
);

function pick() { inputEl.value?.click(); }

async function handleFiles(files) {
  const file = files?.[0];
  if (!file) return;
  fileName.value = file.name;
  emit("select", file);
  try {
    const url = await upload(file);
    emit("update:modelValue", url);
  } catch { /* error via composable */ }
}

function onChange(e) { handleFiles(e.target.files); }
function onDrop(e) { dragOver.value = false; handleFiles(e.dataTransfer?.files); }
function clear() { fileName.value = ""; emit("update:modelValue", ""); if (inputEl.value) inputEl.value.value = ""; }
</script>

<template>
  <div class="uf">
    <div class="uf__label text-body-2 font-weight-semibold mb-1">{{ label }}</div>

    <div
      class="uf__drop"
      :class="{ 'uf__drop--over': dragOver }"
      role="button"
      tabindex="0"
      @click="pick"
      @keydown.enter.prevent="pick"
      @keydown.space.prevent="pick"
      @dragover.prevent="dragOver = true"
      @dragleave.prevent="dragOver = false"
      @drop.prevent="onDrop"
    >
      <input ref="inputEl" type="file" class="uf__input" :accept="defaultAccept" @change="onChange" />

      <div v-if="modelValue && !uploading" class="uf__preview">
        <img v-if="kind === 'image'" :src="modelValue" alt="" class="uf__thumb" />
        <video v-else-if="kind === 'video'" :src="modelValue" class="uf__thumb" controls />
        <a v-else :href="modelValue" target="_blank" rel="noopener" class="uf__link" @click.stop>
          {{ fileName || "View file" }}
        </a>
      </div>

      <p v-else-if="!uploading" class="uf__hint">
        Drag &amp; drop or <span class="text-primary font-weight-semibold">browse</span>
        <span v-if="hint" class="d-block text-caption mt-1">{{ hint }}</span>
      </p>

      <div v-if="uploading" class="uf__progress">
        <VProgressLinear :model-value="progress" color="primary" rounded height="6" />
        <span class="text-caption text-medium-emphasis mt-1">Uploading… {{ progress }}%</span>
      </div>
    </div>

    <div v-if="modelValue && !uploading" class="d-flex gap-2 mt-1">
      <VBtn variant="plain" size="small" @click="pick">Replace</VBtn>
      <VBtn variant="plain" size="small" @click="clear">Remove</VBtn>
    </div>

    <p v-if="error" class="text-error text-caption mt-1">{{ error }}</p>
  </div>
</template>

<style scoped>
.uf { display: flex; flex-direction: column; }
.uf__drop {
  display: flex; align-items: center; justify-content: center;
  min-height: 120px; padding: 1rem;
  border: 1.5px dashed rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px; cursor: pointer; text-align: center;
  transition: border-color 0.15s, background 0.15s;
}
.uf__drop--over { border-color: rgb(var(--v-theme-primary)); background: rgba(var(--v-theme-primary), 0.05); }
.uf__input { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
.uf__preview { display: flex; align-items: center; justify-content: center; width: 100%; }
.uf__thumb { max-height: 160px; max-width: 100%; border-radius: 6px; }
.uf__link { color: rgb(var(--v-theme-primary)); word-break: break-all; }
.uf__hint { margin: 0; color: rgba(var(--v-theme-on-surface), 0.6); font-size: 0.875rem; }
.uf__progress { width: 100%; display: flex; flex-direction: column; align-items: center; }
</style>
